import { memo, useCallback, useMemo, useState } from "react";
import { MuiMaterials } from "../@index.js";
import { getWindowChromeThemeColor, InfoHint } from "./windowChrome/index.js";
import {
    selectedBinsForUi,
    TASK_ACTIONS,
    TASK_DAYS,
    TASK_TIME_SLOTS,
} from "./siteSetupTasks.js";

const HIDE_SCROLLBAR_SX = {
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },
};

function SetupTasksPanel({
    isOpen,
    onToggle,
    isAutoScanOn,
    taskSchedule,
    taskSelectedBins,
    binNames,
    onAutoScanChange,
    onBinsChange,
    onSlotChange,
    border,
    textSecondary,
    paper,
}) {
    const [dayTab, setDayTab] = useState(0);
    const activeDay = TASK_DAYS[dayTab] ?? TASK_DAYS[0];
    const uiSelectedBins = useMemo(
        () => selectedBinsForUi(taskSelectedBins, binNames),
        [taskSelectedBins, binNames]
    );
    const allBinsSelected = uiSelectedBins.length >= binNames.length;

    const handleBinToggle = useCallback((name) => {
        const all = binNames.map((n) => String(n).trim()).filter(Boolean);
        const current = selectedBinsForUi(taskSelectedBins, all);
        const has = current.includes(name);
        const next = has ? current.filter((n) => n !== name) : [...current, name];
        onBinsChange(next.length >= all.length ? [] : next);
    }, [binNames, onBinsChange, taskSelectedBins]);

    const handleSelectAllBins = useCallback(() => {
        onBinsChange([]);
    }, [onBinsChange]);

    const handleClearBins = useCallback(() => {
        onBinsChange(binNames.length ? [binNames[0]] : []);
    }, [binNames, onBinsChange]);

    return (
        <MuiMaterials.Paper
            elevation={0}
            className={`setup-section${isOpen ? " setup-section--open" : ""}`}
            sx={{
                backgroundColor: isOpen
                    ? getWindowChromeThemeColor("#e8e8e8", "#1e1e1e")
                    : "transparent",
                border: "none",
                borderRadius: 0,
                mb: 0,
                overflow: "hidden",
                boxShadow: "none",
            }}
        >
            <MuiMaterials.Box
                sx={{
                    px: { xs: 1.5, sm: 1.75 },
                    py: 1.25,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    userSelect: "none",
                }}
                onClick={onToggle}
            >
                <MuiMaterials.ExpandMoreIcon
                    sx={{
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                    }}
                />
                <MuiMaterials.Typography fontWeight={600} sx={{ fontSize: { xs: 15, sm: 16 } }}>
                    Task
                </MuiMaterials.Typography>
            </MuiMaterials.Box>
            {isOpen ? (
                <MuiMaterials.Box
                    sx={{
                        px: 2,
                        pt: 1.5,
                        pb: 2,
                        backgroundColor: getWindowChromeThemeColor("#e8e8e8", "#1e1e1e"),
                    }}
                >
                    <MuiMaterials.Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        <MuiMaterials.FormControlLabel
                            control={(
                                <MuiMaterials.Checkbox
                                    size="small"
                                    checked={!!isAutoScanOn}
                                    onChange={(e) => onAutoScanChange(e.target.checked)}
                                />
                            )}
                            label={isAutoScanOn ? "Automatic Scan is On" : "Automatic Scan is Off"}
                            sx={{ mr: 0 }}
                        />
                        <InfoHint
                            title="Scheduled report/scan actions run when automatic scan is enabled on the site PC."
                            ariaLabel="Automatic scan help"
                        />
                    </MuiMaterials.Stack>

                    <MuiMaterials.Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ my: 2 }}
                    >
                        <MuiMaterials.Typography variant="body2" fontWeight={600}>
                            Bins
                        </MuiMaterials.Typography>
                        <MuiMaterials.Button size="small" variant="outlined" onClick={handleSelectAllBins}>
                            {allBinsSelected ? "All Bins Selected" : "Select All Bins"}
                        </MuiMaterials.Button>
                        {binNames.length > 1 ? (
                            <MuiMaterials.Button size="small" variant="text" onClick={handleClearBins}>
                                Clear
                            </MuiMaterials.Button>
                        ) : null}
                    </MuiMaterials.Stack>
                    <MuiMaterials.Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 2 }}>
                        {binNames.map((name) => {
                            const selected = uiSelectedBins.includes(name);
                            return (
                                <MuiMaterials.Chip
                                    key={name}
                                    label={name}
                                    size="small"
                                    color={selected ? "primary" : "default"}
                                    variant={selected ? "filled" : "outlined"}
                                    onClick={() => handleBinToggle(name)}
                                />
                            );
                        })}
                        {!binNames.length ? (
                            <MuiMaterials.Stack direction="row" spacing={0.5} alignItems="center">
                                <MuiMaterials.Typography variant="body2" sx={{ color: textSecondary }}>
                                    No bins yet
                                </MuiMaterials.Typography>
                                <InfoHint
                                    title="Add bins first to limit tasks to specific bins."
                                    ariaLabel="Task bins help"
                                />
                            </MuiMaterials.Stack>
                        ) : null}
                    </MuiMaterials.Stack>

                    <MuiMaterials.Tabs
                        value={dayTab}
                        onChange={(_, value) => setDayTab(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ mb: 1, minHeight: 36, borderBottom: `1px solid ${border}` }}
                    >
                        {TASK_DAYS.map((day, index) => (
                            <MuiMaterials.Tab key={day} label={day.slice(0, 3)} value={index} sx={{ minHeight: 36, py: 0.5 }} />
                        ))}
                    </MuiMaterials.Tabs>

                    <MuiMaterials.TableContainer
                        className="hide-scrollbar setup-nested-scroll"
                        sx={{
                            maxHeight: 420,
                            overflowY: "auto",
                            border: `1px solid ${border}`,
                            borderRadius: 1,
                            backgroundColor: paper,
                            ...HIDE_SCROLLBAR_SX,
                        }}
                    >
                        <MuiMaterials.Table size="small" stickyHeader>
                            <MuiMaterials.TableHead>
                                <MuiMaterials.TableRow>
                                    <MuiMaterials.TableCell sx={{ fontWeight: 600, fontSize: 12, width: 88 }}>
                                        Time
                                    </MuiMaterials.TableCell>
                                    {TASK_ACTIONS.map((action) => (
                                        <MuiMaterials.TableCell
                                            key={action.key}
                                            align="center"
                                            sx={{ fontWeight: 600, fontSize: 12, px: 0.5 }}
                                        >
                                            <MuiMaterials.Tooltip title={action.tooltip} arrow>
                                                <span>{action.label}</span>
                                            </MuiMaterials.Tooltip>
                                        </MuiMaterials.TableCell>
                                    ))}
                                </MuiMaterials.TableRow>
                            </MuiMaterials.TableHead>
                            <MuiMaterials.TableBody>
                                {TASK_TIME_SLOTS.map((time) => {
                                    const slot = taskSchedule?.[activeDay]?.[time] ?? {};
                                    return (
                                        <MuiMaterials.TableRow key={`${activeDay}-${time}`} hover>
                                            <MuiMaterials.TableCell sx={{ fontSize: 12, whiteSpace: "nowrap", py: 0.25 }}>
                                                {time}
                                            </MuiMaterials.TableCell>
                                            {TASK_ACTIONS.map((action) => (
                                                <MuiMaterials.TableCell key={action.key} align="center" sx={{ py: 0, px: 0.25 }}>
                                                    <MuiMaterials.Checkbox
                                                        size="small"
                                                        checked={!!slot[action.key]}
                                                        onChange={(e) => onSlotChange(activeDay, time, action.key, e.target.checked)}
                                                        sx={{ p: 0.25 }}
                                                    />
                                                </MuiMaterials.TableCell>
                                            ))}
                                        </MuiMaterials.TableRow>
                                    );
                                })}
                            </MuiMaterials.TableBody>
                        </MuiMaterials.Table>
                    </MuiMaterials.TableContainer>
                    <MuiMaterials.Typography variant="caption" sx={{ color: textSecondary, mt: 1, display: "block" }}>
                        P = Print comparison, Email = email comparison, GMS Print/Email = GMS report tasks (BTS Configure Tasks).
                    </MuiMaterials.Typography>
                </MuiMaterials.Box>
            ) : null}
        </MuiMaterials.Paper>
    );
}

export default memo(SetupTasksPanel);
